import { Injectable, computed, inject, signal } from "@angular/core";
import { Subscription, interval } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { connectRunStream } from "./connect-run-agent";
import { AgentKitService } from "../../../agent-ui";
import { AgentsApiPrivateService } from "./agents-api-private.service";
import { DebugStreamService } from "./debug-stream.service";
import { isTerminalStatus, mapRunStatus } from "./background-job-status.util";
import type {
  BackgroundJobBlockData,
  BackgroundJobSnapshot,
  BackgroundJobTrackingMode,
  DeepAnalysisActivity,
  JobProgress,
  OutputRef,
  PipelinePhaseState,
  PipelinePhaseStep,
  StepResult,
  SubAgentTodo,
} from "../model/block.types";
import { deriveSubAgentLabel, isSubAgentToolCallEvent, isTodoUpdateEvent } from "../model/block.types";

/** How long a completed/errored job stays visible in the panel before being removed. */
const PANEL_GRACE_MS = 2000;

/** Default poll interval when a block doesn't specify `poll_interval_ms`. */
const DEFAULT_POLL_INTERVAL_MS = 3000;

/**
 * Frontend-only default when a block doesn't specify `tracking_mode`. Change this to shift every
 * job's tracking strategy fleet-wide — e.g. to "both" or "polling" if the backend goes multi-pod
 * and SSE stops being reliably reachable from the pod that owns a given run. That's an infra
 * characteristic, not a per-job decision, so it's fine for the backend to leave `tracking_mode`
 * unset and let this default carry every job.
 */
const DEFAULT_TRACKING_MODE: BackgroundJobTrackingMode = "sse";

/** `job_progress.status` values that mean the run is over — stop polling/streaming it. */
const TERMINAL_JOB_PROGRESS_STATUSES: ReadonlySet<string> = new Set(["completed", "error", "cancelled"]);

function isTerminalJobProgress(progress: JobProgress | null): boolean {
  return !!progress && TERMINAL_JOB_PROGRESS_STATUSES.has(progress.status);
}

/** Maps `job_progress.status` to the run-status vocabulary the rest of the snapshot uses. */
function mapJobProgressToRunStatus(progress: JobProgress): BackgroundJobSnapshot["run_status"] {
  switch (progress.status) {
    case "completed":
      return "success";
    case "error":
      return "error";
    case "running":
      return "running";
    case "pending":
      return "pending";
    default:
      return null;
  }
}

/**
 * Coarse "how far along" ordering for a `job_progress` snapshot — phases_completed first, then
 * whether the current phase itself is done. Used only to arbitrate between SSE and polling in
 * "both" mode: a later update should never make progress look like it went backwards.
 */
function jobProgressRank(progress: JobProgress): number {
  const statusRank = progress.status === "pending" ? 0 : progress.status === "running" ? 1 : 2;
  return progress.phases_completed * 10 + statusRank;
}

/**
 * Upserts a `pipeline_phase_step` CUSTOM event payload into a phase's step list — creates
 * a new step or updates one in place when `step` already matches (each of discovery's 9
 * nodes / report's 2 nodes fires running → completed/error for the same `step` key).
 */
function upsertPhaseStep(steps: PipelinePhaseStep[], value: any): PipelinePhaseStep[] {
  const step: PipelinePhaseStep = {
    step: value?.step ?? "",
    label: value?.label || value?.step || "Step",
    status: value?.status ?? "running",
    message: value?.message ?? null,
  };
  const idx = steps.findIndex((s) => s.step === step.step);
  if (idx === -1) return [...steps, step];
  const next = [...steps];
  next[idx] = step;
  return next;
}

/**
 * Builds the phase→steps map a snapshot renders from, merging two sources: `job_progress.phases[].steps`
 * (persisted, terminal-only results — the only source available right after a reload, before any SSE
 * event has arrived) and the live SSE overlay (may include in-flight "running" steps, always fresher for
 * a phase once it starts emitting). Persisted steps seed each phase; live steps upsert on top by `step`
 * key so a reload never regresses a phase that's still actively streaming.
 */
function mergePhaseSteps(
  jobProgress: JobProgress | null,
  liveStepsByPhase: Record<string, PipelinePhaseStep[]>,
): Record<string, PipelinePhaseStep[]> {
  const merged: Record<string, PipelinePhaseStep[]> = {};
  for (const phase of jobProgress?.phases ?? []) {
    if (phase.steps?.length) merged[phase.name] = phase.steps.map(toPipelinePhaseStep);
  }
  for (const [phaseName, liveSteps] of Object.entries(liveStepsByPhase)) {
    merged[phaseName] = liveSteps.reduce((acc, step) => upsertPhaseStep(acc, step), merged[phaseName] ?? []);
  }
  return merged;
}

/**
 * Drops the redundant `phase` field — the map key already carries it — for uniform rendering with live
 * steps. `status` is cast: persisted steps are contractually always "completed"/"error", a subset of both
 * `JobStatus` and `PipelinePhaseEventStatus`.
 */
function toPipelinePhaseStep(step: StepResult): PipelinePhaseStep {
  return { step: step.step, label: step.label, status: step.status as unknown as PipelinePhaseStep["status"], message: step.message };
}

/** A block that still carries the fields needed to poll/stream its run. */
type LiveBackgroundJobBlockData = BackgroundJobBlockData &
  Required<Pick<BackgroundJobBlockData, "job_thread_id" | "job_run_id" | "assistant_id">>;

/**
 * True while the block still has its live routing fields. Once the backend applies
 * its completion patch, `data` is replaced with a reduced shape (job_id, job_progress,
 * total_records, pipeline_summary only) — these fields are gone for good.
 */
function hasRoutingInfo(block: BackgroundJobBlockData): block is LiveBackgroundJobBlockData {
  return !!block.job_thread_id && !!block.job_run_id && !!block.assistant_id;
}

@Injectable({ providedIn: "root" })
export class BackgroundJobTrackerService {
  readonly #agentKit = inject(AgentKitService);

  readonly #agentsApi = inject(AgentsApiPrivateService);

  readonly #debugStream = inject(DebugStreamService);

  readonly #jobs = signal<Map<string, BackgroundJobSnapshot>>(new Map());

  /** Active SSE subscriptions — keyed by job_id. */
  readonly #subscriptions = new Map<string, Subscription>();

  /** Active polling subscriptions — keyed by job_id. Independent of #subscriptions (SSE), both may be live at once in "both" mode. */
  readonly #pollSubscriptions = new Map<string, Subscription>();

  /**
   * Last `job_progress` accepted into `#sseStateByJob`, plus which channel it came from — keyed
   * by job_id. Only consulted in "both" mode, to arbitrate between SSE and polling updates (see
   * `#applyStateUpdate`); irrelevant noise for "sse"/"polling"-only jobs since there's nothing to
   * arbitrate against.
   */
  readonly #progressMetaByJob = new Map<string, { progress: JobProgress; source: "sse" | "poll" }>();

  readonly #graceTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Job IDs that have reached a terminal state (success / error / timeout).
   * Kept permanently so re-parses of the same block don't restart tracking.
   */
  readonly #terminalJobIds = new Set<string>();

  /**
   * Snapshots preserved after a terminal job leaves the active signal.
   * Allows getSnapshot() to restore final state when a component remounts.
   */
  readonly #terminalSnapshots = new Map<string, BackgroundJobSnapshot>();

  /** Latest ag-ui accumulated state from SSE — keyed by job_id. */
  readonly #sseStateByJob = new Map<string, Record<string, unknown>>();

  /** Latest `*_todo_update` CUSTOM event payload (see `isTodoUpdateEvent`) — keyed by job_id. Replaced wholesale. */
  readonly #subAgentTodosByJob = new Map<string, SubAgentTodo[]>();

  /** Sub-agent name derived from the last `*_todo_update`/`*_tool_call` event name (see `deriveSubAgentLabel`) — keyed by job_id. */
  readonly #subAgentLabelByJob = new Map<string, string>();

  /** Latest `pipeline_phase_status` status+message per phase, from CUSTOM events — keyed by job_id. */
  readonly #phaseStatesByJob = new Map<string, Record<string, PipelinePhaseState>>();

  /** `pipeline_phase_step` progress per phase, upserted by step — keyed by job_id. */
  readonly #phaseStepsByJob = new Map<string, Record<string, PipelinePhaseStep[]>>();

  /** Live deep_analysis tool-call/message-chunk activity — keyed by job_id. */
  readonly #deepAnalysisByJob = new Map<string, DeepAnalysisActivity>();

  /**
   * Job IDs currently resolving their initial run status (getRunStatus in flight).
   * Guards against duplicate hydration calls when startJob() is re-invoked for the
   * same job before the first status check resolves (e.g. an unrelated snapshot
   * mutation re-fires the parsing effect while hydration is still pending).
   */
  readonly #pendingHydration = new Set<string>();

  /** Live array of all currently tracked job snapshots (including grace-period completions). */
  readonly activeJobs = computed(() => Array.from(this.#jobs().values()));

  /**
   * Starts tracking a background job if not already tracked.
   * Idempotent: subsequent calls with the same job_id are no-ops.
   *
   * @param onComplete Optional callback invoked once on terminal status "success" or "error".
   */
  startJob(block: BackgroundJobBlockData, onComplete?: () => void): void {
    if (
      this.#terminalJobIds.has(block.job_id) ||
      this.#subscriptions.has(block.job_id) ||
      this.#pollSubscriptions.has(block.job_id) ||
      this.#pendingHydration.has(block.job_id)
    ) {
      return;
    }
    if (!hasRoutingInfo(block)) {
      // Completion patch already applied — job is terminal and self-describes via
      // job_progress/pipeline_summary/total_records. The routing fields needed to
      // poll/stream are gone by design once a job is done, so build straight from it.
      this.#hydrateFromPatchedBlock(block, onComplete);
      return;
    }
    this.#hydrateJob(block, onComplete);
  }

  /**
   * Returns the latest snapshot for a given job, or null if not yet received.
   * Reactive: tracking is active when called inside a computed() or effect().
   */
  getSnapshot(jobId: string): BackgroundJobSnapshot | null {
    return this.#jobs().get(jobId) ?? this.#terminalSnapshots.get(jobId) ?? null;
  }

  // ── Hydration (resolve current run status before choosing a tracking path) ─

  /**
   * Builds a terminal snapshot directly from an already-patched block — no network
   * call needed or possible, since the routing fields are gone. Falls back to an
   * explicit error state if the patched block doesn't even carry job_progress
   * (unexpected/malformed data — nothing left to render from).
   */
  #hydrateFromPatchedBlock(block: BackgroundJobBlockData, onComplete?: () => void): void {
    const snap = this.#buildSnapshotFromSseState(block as unknown as Record<string, unknown>, block);
    const finalSnap: BackgroundJobSnapshot =
      snap.run_status === null
        ? { ...snap, run_status: "error", run_error_message: "Job data is incomplete and cannot be tracked." }
        : snap;
    this.#commitTerminalSnapshot(block.job_id, finalSnap);
    onComplete?.();
  }

  /**
   * A block can arrive either as a brand-new job (started this session) or as part
   * of a historical conversation (page reload, thread switch). Either way there is
   * no live ag-ui state to render from yet, so the first thing done for any unseen
   * job_id is a single cheap run-status check — terminal jobs are hydrated with one
   * state fetch, still-active jobs are picked up live via #startTracking.
   */
  #hydrateJob(block: LiveBackgroundJobBlockData, onComplete?: () => void): void {
    this.#pendingHydration.add(block.job_id);

    this.#agentsApi
      .getRunStatus(block.job_thread_id, block.job_run_id)
      .pipe(take(1))
      .subscribe({
        next: ({ status, error_message }) => {
          this.#pendingHydration.delete(block.job_id);
          if (isTerminalStatus(status)) {
            this.#hydrateTerminalJob(block, status, error_message, onComplete);
          } else {
            this.#startTracking(block, onComplete);
          }
        },
        error: (err) => {
          this.#pendingHydration.delete(block.job_id);
          console.warn(
            `BackgroundJobTrackerService: run status check failed, falling back to tracking job_id=${block.job_id}`,
            err,
          );
          this.#startTracking(block, onComplete);
        },
      });
  }

  /**
   * Picks the live-tracking path(s) for a non-terminal job per `tracking_mode` (backend override
   * on the block, else `DEFAULT_TRACKING_MODE`): SSE only, polling only, or both at once. In
   * "both" mode both channels write into the same per-job state (arbitrated by
   * `#applyStateUpdate`) and either one reaching terminal ends the job — `fireOnce` guards
   * `onComplete` so it never fires twice when both detect the same completion.
   */
  #startTracking(block: LiveBackgroundJobBlockData, onComplete?: () => void): void {
    const mode = block.tracking_mode ?? DEFAULT_TRACKING_MODE;
    let fired = false;
    const fireOnce = () => {
      if (fired) return;
      fired = true;
      onComplete?.();
    };
    if (mode === "sse" || mode === "both") this.#startJobSse(block, fireOnce);
    if (mode === "polling" || mode === "both") this.#startJobPolling(block, fireOnce);
  }

  /** Hydrates an already-terminal job with a single canonical state fetch — no stream needed. */
  #hydrateTerminalJob(
    block: BackgroundJobBlockData,
    rawStatus: string,
    runErrorMessage: string | null,
    onComplete?: () => void,
  ): void {
    this.#agentsApi
      .getThreadState(block.job_thread_id)
      .pipe(take(1))
      .subscribe({
        next: (rawState) => {
          const values = rawState.values as Record<string, unknown>;
          const jobProgress = (values.job_progress as JobProgress) ?? null;
          const mappedStatus = mapRunStatus(rawStatus);
          // Mirrors BackgroundJobPollingService: LangGraph can mark the run "success" while
          // the pipeline's own job_progress reports an internal error (e.g. no_discover_results).
          const runStatus: BackgroundJobSnapshot["run_status"] =
            mappedStatus === "success" && jobProgress?.status === "error" ? "error" : mappedStatus;

          const snap: BackgroundJobSnapshot = {
            ...this.#buildSnapshotFromSseState(values, block, runErrorMessage),
            run_status: runStatus,
          };
          this.#commitTerminalSnapshot(block.job_id, snap);
          onComplete?.();
        },
        error: (err) => {
          console.error(`BackgroundJobTrackerService: terminal state hydration failed job_id=${block.job_id}`, err);
          const snap: BackgroundJobSnapshot = {
            ...this.#buildSnapshotFromSseState({}, block, "Failed to load final job state."),
            run_status: "error",
          };
          this.#commitTerminalSnapshot(block.job_id, snap);
          onComplete?.();
        },
      });
  }

  // ── SSE path (CopilotKit proxy → agent/run/stream) ───────────────────────

  #startJobSse(block: LiveBackgroundJobBlockData, onComplete?: () => void): void {
    const runtimeUrl = this.#agentKit.runtimeUrl();
    if (!runtimeUrl) {
      console.warn("BackgroundJobTrackerService: runtimeUrl not set, cannot start SSE", block.job_id);
      return;
    }

    let onCompleteFired = false;
    let terminalSeen = false;

    console.log(`[BgJobStream] connecting job_id=${block.job_id} run_id=${block.job_run_id}`);

    const sub = connectRunStream({
      url: runtimeUrl,
      headers: this.#agentKit.headers(),
      agentId: block.assistant_id,
      threadId: block.job_thread_id,
      runId: block.job_run_id,
    }).subscribe({
      next: (item) => {
        try {
          if (item.type === "state") {
            this.#debugStream.logPipelineEvent(block.job_id, "STATE", item.state);
            this.#applyStateUpdate(block.job_id, item.state, "sse");
            const snap = this.#buildSnapshotFromSseState(this.#sseStateByJob.get(block.job_id) ?? item.state, block);
            this.#jobs.update((map) => new Map(map).set(block.job_id, snap));
          } else if (item.type === "custom") {
            this.#debugStream.logPipelineEvent(block.job_id, `CUSTOM:${item.name}`, item.value);
            const value = item.value as any;
            if (isTodoUpdateEvent(item.name)) {
              const todos = (value?.todos as SubAgentTodo[] | undefined) ?? [];
              this.#subAgentTodosByJob.set(block.job_id, todos);
              this.#subAgentLabelByJob.set(block.job_id, deriveSubAgentLabel(item.name));
            } else if (item.name === "pipeline_phase_status") {
              const phase = value?.phase;
              if (!phase) return;
              const current = this.#phaseStatesByJob.get(block.job_id) ?? {};
              this.#phaseStatesByJob.set(block.job_id, {
                ...current,
                [phase]: { status: value?.status ?? "running", message: value?.message ?? null },
              });
            } else if (item.name === "pipeline_phase_step") {
              const phase = value?.phase;
              if (!phase) return;
              const current = this.#phaseStepsByJob.get(block.job_id) ?? {};
              const steps = current[phase] ?? [];
              this.#phaseStepsByJob.set(block.job_id, { ...current, [phase]: upsertPhaseStep(steps, value) });
            } else if (isSubAgentToolCallEvent(item.name)) {
              const activity = this.#deepAnalysisByJob.get(block.job_id) ?? { toolCall: null, message: "" };
              this.#deepAnalysisByJob.set(block.job_id, {
                ...activity,
                toolCall: { name: value?.tool_name ?? "", label: value?.label || "Working…" },
              });
              this.#subAgentLabelByJob.set(block.job_id, deriveSubAgentLabel(item.name));
            } else if (item.name === "deep_analysis_message_chunk") {
              const activity = this.#deepAnalysisByJob.get(block.job_id) ?? { toolCall: null, message: "" };
              this.#deepAnalysisByJob.set(block.job_id, { ...activity, message: activity.message + (value?.text ?? "") });
            } else {
              return; // unrecognized custom event — nothing to update, skip the snapshot rebuild
            }
            const lastState = this.#sseStateByJob.get(block.job_id) ?? {};
            const snap = this.#buildSnapshotFromSseState(lastState, block);
            this.#jobs.update((map) => new Map(map).set(block.job_id, snap));
          } else if (item.type === "finished") {
            this.#debugStream.logPipelineEvent(block.job_id, "RUN_FINISHED", null);
            terminalSeen = true;
            console.log(`[BgJobStream] RUN_FINISHED job_id=${block.job_id}`);
            if (!onCompleteFired) {
              onCompleteFired = true;
              onComplete?.();
            }
            this.#onSseTerminal(block, "success", null);
          } else if (item.type === "run_error") {
            this.#debugStream.logPipelineEvent(block.job_id, "RUN_ERROR", null);
            terminalSeen = true;
            console.log(`[BgJobStream] RUN_ERROR job_id=${block.job_id}`);
            if (!onCompleteFired) {
              onCompleteFired = true;
              onComplete?.();
            }
            this.#onSseTerminal(block, "error", null);
          } else if (item.type === "failed") {
            this.#debugStream.logPipelineEvent(block.job_id, "FAILED", item.error);
            console.error(`[BgJobStream] run failed job_id=${block.job_id}`, item.error);
            if (terminalSeen) return; // already handled by RUN_FINISHED / RUN_ERROR
            // SSE failed before terminal event (e.g. run already completed, proxy returned 400).
            // Fetch current thread state once to determine actual terminal status.
            this.#agentsApi
              .getThreadState(block.job_thread_id)
              .pipe(take(1))
              .subscribe({
                next: (rawState) => {
                  const values = rawState.values as Record<string, unknown>;
                  const snap = this.#buildSnapshotFromSseState(values, block);
                  const terminalStatus: "success" | "error" =
                    snap.progress?.status === "completed" ? "success" : "error";
                  if (!onCompleteFired) {
                    onCompleteFired = true;
                    onComplete?.();
                  }
                  this.#onSseTerminal(block, terminalStatus, null);
                },
                error: (fetchErr) => {
                  console.error(`[BgJobStream] fallback state fetch failed job_id=${block.job_id}`, fetchErr);
                  if (!onCompleteFired) {
                    onCompleteFired = true;
                    onComplete?.();
                  }
                  this.#onSseTerminal(block, "error", null);
                },
              });
          }
        } catch (err) {
          console.error(
            `[BgJobStream] DEBUG: exception in next() handler job_id=${block.job_id}, item.type=${item.type}` +
              ` — this throw unsubscribes the stream and triggers abortRun(), closing the SSE connection`,
            err,
            item,
          );
          throw err; // rethrow so behavior is unchanged; log above exists purely to confirm root cause
        }
      },
      error: (err) => {
        console.error(
          `[BgJobStream] DEBUG: SSE subscription errored (this is what closes the connection) job_id=${block.job_id}`,
          err,
        );
      },
    });

    this.#subscriptions.set(block.job_id, sub);
  }

  // ── Polling path (getThreadState on a timer) ─────────────────────────────

  /**
   * Polling path — refreshes `job_progress` on a timer via `getThreadState`, independent of SSE.
   * Used alone when `tracking_mode` is "polling" (no SSE connection at all — the only option that
   * fully sidesteps a multi-pod SSE stickiness problem), or alongside SSE when "both". Once
   * `job_progress` reports terminal the `#terminalJobIds` guard makes further ticks a no-op
   * (never re-commits or regresses the final snapshot) — the timer itself is torn down by
   * `#removeJob` after the grace period, mirroring how the SSE subscription is cleaned up.
   */
  #startJobPolling(block: LiveBackgroundJobBlockData, onComplete?: () => void): void {
    const pollMs = block.poll_interval_ms ?? DEFAULT_POLL_INTERVAL_MS;

    const sub = interval(pollMs)
      .pipe(switchMap(() => this.#agentsApi.getThreadState(block.job_thread_id)))
      .subscribe({
        next: (rawState) => {
          if (this.#terminalJobIds.has(block.job_id)) return;

          const values = rawState.values as Record<string, unknown>;
          this.#applyStateUpdate(block.job_id, values, "poll");
          const snap = this.#buildSnapshotFromSseState(this.#sseStateByJob.get(block.job_id) ?? values, block);
          this.#jobs.update((map) => new Map(map).set(block.job_id, snap));

          if (isTerminalJobProgress(snap.progress)) {
            this.#commitTerminalSnapshot(block.job_id, { ...snap, run_status: mapJobProgressToRunStatus(snap.progress!) });
            onComplete?.();
          }
        },
        error: (err) => console.error(`BackgroundJobTrackerService: polling failed job_id=${block.job_id}`, err),
      });

    this.#pollSubscriptions.set(block.job_id, sub);
  }

  /**
   * Arbitrates `job_progress` between SSE and polling when both track the same job ("both"
   * mode) — an update only replaces the stored raw state if its `job_progress` hasn't regressed
   * (see `jobProgressRank`). A regression means the two channels disagree on how far along the
   * run is (e.g. a dropped CUSTOM event, or SSE reading a stale pod's checkpoint) — that's logged
   * so it can be chased down on the backend instead of silently flickering the UI backwards.
   * For "sse"-only or "polling"-only jobs there's nothing to arbitrate against (no prior entry
   * from the other channel), so every update just goes through.
   */
  #applyStateUpdate(jobId: string, values: Record<string, unknown>, source: "sse" | "poll"): void {
    const incoming = (values.job_progress as JobProgress) ?? null;
    if (!incoming) {
      this.#sseStateByJob.set(jobId, values);
      return;
    }

    const stored = this.#progressMetaByJob.get(jobId);
    if (stored && stored.source !== source && jobProgressRank(incoming) < jobProgressRank(stored.progress)) {
      console.warn(
        `BackgroundJobTrackerService: job_progress discrepancy job_id=${jobId} — ${source} update is behind ` +
          `${stored.source}'s last state, ignoring`,
        { incoming, stored: stored.progress },
      );
      return;
    }

    this.#progressMetaByJob.set(jobId, { progress: incoming, source });
    this.#sseStateByJob.set(jobId, values);
  }

  /** Builds a BackgroundJobSnapshot from an ag-ui accumulated state object. */
  #buildSnapshotFromSseState(
    state: Record<string, unknown>,
    block: BackgroundJobBlockData,
    runErrorMessage: string | null = null,
  ): BackgroundJobSnapshot {
    const jobProgress = (state.job_progress as JobProgress) ?? null;
    const runStatus: BackgroundJobSnapshot["run_status"] = jobProgress ? mapJobProgressToRunStatus(jobProgress) : null;

    return {
      block,
      progress: jobProgress,
      output_refs: (state.output_refs as OutputRef[]) ?? [],
      run_status: runStatus,
      run_error_message: runErrorMessage,
      state_debug:
        state.providers !== undefined
          ? {
              providers: state.providers ?? null,
              pipeline_summary: state.pipeline_summary ?? null,
              pipeline_report_data: state.pipeline_report_data ?? null,
              query: state.query ?? null,
            }
          : undefined,
      subAgentTodos: this.#subAgentTodosByJob.get(block.job_id) ?? [],
      subAgentLabel: this.#subAgentLabelByJob.get(block.job_id) ?? null,
      phaseStates: this.#phaseStatesByJob.get(block.job_id) ?? {},
      phaseSteps: mergePhaseSteps(jobProgress, this.#phaseStepsByJob.get(block.job_id) ?? {}),
      deepAnalysis: this.#deepAnalysisByJob.get(block.job_id) ?? { toolCall: null, message: "" },
    };
  }

  /** Stamps final terminal snapshot and starts the grace timer for panel removal. */
  #onSseTerminal(
    block: BackgroundJobBlockData,
    terminalStatus: "success" | "error",
    runErrorMessage: string | null,
  ): void {
    if (this.#terminalJobIds.has(block.job_id)) return; // already committed by the polling side in "both" mode
    const lastState = this.#sseStateByJob.get(block.job_id) ?? {};
    const finalSnap: BackgroundJobSnapshot = {
      ...this.#buildSnapshotFromSseState(lastState, block, runErrorMessage),
      run_status: terminalStatus,
    };
    this.#commitTerminalSnapshot(block.job_id, finalSnap);
  }

  /**
   * Writes a terminal snapshot to the reactive #jobs signal (so any computed() reading
   * getSnapshot() picks it up immediately) and schedules the grace-period migration into
   * #terminalSnapshots. #terminalSnapshots itself is a plain Map — writing to it directly
   * without going through #jobs first would leave existing card components stuck on a
   * stale computed() value, since plain Map reads aren't tracked by Angular's reactivity.
   */
  #commitTerminalSnapshot(jobId: string, snap: BackgroundJobSnapshot): void {
    this.#jobs.update((map) => new Map(map).set(jobId, snap));
    this.#terminalJobIds.add(jobId);
    const timer = setTimeout(() => {
      const current = this.#jobs().get(jobId);
      if (current) this.#terminalSnapshots.set(jobId, current);
      this.#graceTimers.delete(jobId);
      this.#removeJob(jobId);
    }, PANEL_GRACE_MS);
    this.#graceTimers.set(jobId, timer);
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  #removeJob(jobId: string): void {
    this.#subscriptions.get(jobId)?.unsubscribe(); // triggers abortRun() via teardown
    this.#subscriptions.delete(jobId);
    this.#pollSubscriptions.get(jobId)?.unsubscribe();
    this.#pollSubscriptions.delete(jobId);
    this.#progressMetaByJob.delete(jobId);
    this.#sseStateByJob.delete(jobId);
    this.#subAgentTodosByJob.delete(jobId);
    this.#subAgentLabelByJob.delete(jobId);
    this.#phaseStatesByJob.delete(jobId);
    this.#phaseStepsByJob.delete(jobId);
    this.#deepAnalysisByJob.delete(jobId);
    this.#jobs.update((map) => {
      const next = new Map(map);
      next.delete(jobId);
      return next;
    });
  }
}
